import i18n from '@/utils/i18n';
import { AttachmentListHTML } from '@/components/attachment-list/attachment-list-html';
import { type FC, useCallback, useState } from 'react';
import {
  getSubjectTranslation,
  PROCESSED_STATUSES,
} from '@/screens/order-inbounds/utils';
import {
  type ImageMapArea,
  type InboundAttachment,
} from '@/components/attachment-list/attachment-list-types';
import { OrderInboundsDetailEmlContent } from './order-inbounds-detail-eml-content/order-inbounds-detail-eml-content';
import { type OrderMetadata } from '@/screens/order-inbounds/types';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useLayoutContext } from '@/contexts/layout-context';
import { ZoomableImageMap } from '@/screens/order-inbounds/zoomable-image-map';
import type { OnAttachmentListItemPressCallback } from './types';

const CONTAINER_PADDING = 24;

interface OrderInboundsDetailTabContentProps {
  attachment: InboundAttachment;
  containerWidth: number;
  onAreaSelect: (area: ImageMapArea) => void;
  onEMLAttachmentListItemPress: OnAttachmentListItemPressCallback;
  onEmlError?: (attachment: InboundAttachment) => void;
  onHighlightElementSelect: (searchInputUUID: string) => void;
  onHtmlHasNoContent?: (attachment: InboundAttachment) => void;
  onInlineImageClick?: (inboundContentId: string, index: number) => void;
  order: OrderMetadata;
  selectedAreaID?: string;
  showTranslation?: boolean;
  translationToggle?: React.ReactNode;
}

export const OrderInboundsDetailTabContent: FC<
  OrderInboundsDetailTabContentProps
> = ({
  attachment,
  containerWidth,
  onAreaSelect,
  onEMLAttachmentListItemPress,
  onEmlError,
  onHighlightElementSelect,
  onHtmlHasNoContent,
  onInlineImageClick,
  order,
  selectedAreaID,
  showTranslation,
  translationToggle,
}) => {
  const { isMediumLayout, isSmallTabletLayout } = useLayoutContext();

  const [zoomLevelByUrl, setZoomLevelByUrl] = useState<Record<string, number>>(
    {},
  );

  const handleZoomChange = useCallback(
    (attachment: InboundAttachment, newLevel: number) => {
      setZoomLevelByUrl((prev) => ({
        ...prev,
        [attachment.url]: newLevel,
      }));
    },
    [],
  );

  const renderContent = useCallback(
    (attachment: InboundAttachment) => {
      const realContainerWidth = containerWidth - CONTAINER_PADDING * 2;

      // On medium and small tablet layouts, add bottom offset to zoom controls
      // so they appear above the Add/Continue buttons
      const zoomControlsBottomOffset =
        isMediumLayout || isSmallTabletLayout ? 88 : undefined;

      switch (attachment.type) {
        case 'EML':
          return (
            <OrderInboundsDetailEmlContent
              attachment={attachment}
              collapsible={false}
              containerWidth={realContainerWidth}
              HeaderComponent={
                <>
                  {Boolean(order.subject) ? (
                    <View style={[styles.headerContainer]}>
                      <Typography variant="heading-md">
                        {showTranslation
                          ? (getSubjectTranslation(order) ?? order.subject)
                          : order.subject}
                      </Typography>
                    </View>
                  ) : null}
                </>
              }
              onAreaSelect={onAreaSelect}
              onAttachmentListItemPress={onEMLAttachmentListItemPress}
              onEmlError={() => onEmlError?.(attachment)}
              onHighlightElementSelect={onHighlightElementSelect}
              onHtmlHasNoContent={() => onHtmlHasNoContent?.(attachment)}
              onInlineImageClick={onInlineImageClick}
              order={order}
              selectedAreaID={selectedAreaID}
              showTranslation={showTranslation}
              translationToggle={translationToggle}
            />
          );
        case 'HTML':
          return (
            <AttachmentListHTML
              htmlURL={attachment.url}
              onHighlightElementSelect={onHighlightElementSelect}
              selectedSearchInputUUID={selectedAreaID}
              showHighlights={!PROCESSED_STATUSES.has(order.status)}
              width={realContainerWidth}
            />
          );
        case 'IMAGE_MAP':
          return (
            <ZoomableImageMap
              containerWidth={realContainerWidth}
              images={[{ areas: attachment.areas, url: attachment.url }]}
              onAreaSelect={onAreaSelect}
              selectedAreaID={selectedAreaID}
              setZoomLevel={(zoom) => handleZoomChange(attachment, zoom)}
              zoomControlsBottomOffset={zoomControlsBottomOffset}
              zoomLevel={zoomLevelByUrl[attachment.url] ?? 1}
            />
          );
        case 'INLINE_IMAGE': {
          const imageMapAttachment = order.attachments?.find(
            (att) => att.type === 'IMAGE_MAP' && att.url === attachment.url,
          );

          const areas =
            imageMapAttachment?.type === 'IMAGE_MAP'
              ? imageMapAttachment.areas
              : [];

          return (
            <ZoomableImageMap
              containerWidth={realContainerWidth}
              images={[
                {
                  areas,
                  url: attachment.url,
                },
              ]}
              onAreaSelect={onAreaSelect}
              selectedAreaID={selectedAreaID}
              setZoomLevel={(zoom) => handleZoomChange(attachment, zoom)}
              zoomControlsBottomOffset={zoomControlsBottomOffset}
              zoomLevel={zoomLevelByUrl[attachment.url] ?? 1}
            />
          );
        }
        default:
          return (
            <View style={styles.fallbackContainer}>
              <Text>{i18n.t('order_inbound.unsupported_attachment_type')}</Text>
            </View>
          );
      }
    },
    [
      containerWidth,
      handleZoomChange,
      isMediumLayout,
      isSmallTabletLayout,
      onAreaSelect,
      onEmlError,
      onHighlightElementSelect,
      onHtmlHasNoContent,
      onInlineImageClick,
      order,
      selectedAreaID,
      showTranslation,
      translationToggle,
      zoomLevelByUrl,
      onEMLAttachmentListItemPress,
    ],
  );

  return (
    <ScrollView contentContainerStyle={styles.scrollContent}>
      {renderContent(attachment)}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  fallbackContainer: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  headerContainer: {
    paddingBottom: 24,
  },
  scrollContent: {
    flexGrow: 1,
    gap: 8,
    padding: CONTAINER_PADDING,
  },
});
